import { useEffect, useState } from 'react';

import { AlertCircle, Calendar, FileText, MessageCircle, Settings, Users, X } from 'lucide-react';
import { NavLink } from 'react-router';

import chartaiLogoLight from '@/assets/chartai-logo-light.svg';
import { cn } from '@/lib/utils';
import { UnderConstructionDialog } from '@/shared/components/UnderConstructionDialog';
import { useSidebarStore } from '@/shared/stores/sidebarStore';
import { useProfileStore } from '@/shared/stores/useProfileStore';

interface NavItem {
  icon: typeof Calendar;
  label: string;
  to: string;
  badge?: number;
  underConstruction?: boolean;
}

function buildSections(needsReviewCount: number): readonly { items: readonly NavItem[] }[] {
  return [
    {
      items: [
        { icon: Calendar, label: 'Today', to: '/dashboard/appointments' },
        { icon: Users, label: 'Patients', to: '/dashboard/patients' },
      ],
    },
    {
      items: [
        {
          icon: AlertCircle,
          label: 'Ready for Review',
          to: '/dashboard/review-queue',
          badge: needsReviewCount,
        },
        { icon: FileText, label: 'Templates', to: '/dashboard/templates' },
        // Documents — hidden for now
        // System section (Exports, Integrations) — hidden for now
      ],
    },
    {
      items: [{ icon: Settings, label: 'Settings', to: '/dashboard/settings' }],
    },
  ];
}

interface MobileSidebarProps {
  needsReviewCount?: number;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

export function MobileSidebar({ needsReviewCount = 0 }: MobileSidebarProps = {}) {
  const sections = buildSections(needsReviewCount);
  const mobileOpen = useSidebarStore((s) => s.mobileOpen);
  const closeMobile = useSidebarStore((s) => s.closeMobile);
  const selectedProfile = useProfileStore((s) => s.selectedProfile);
  const [constructionFeature, setConstructionFeature] = useState<string | null>(null);

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobile();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('overflow-hidden');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('overflow-hidden');
    };
  }, [mobileOpen, closeMobile]);

  if (!mobileOpen) {
    return null;
  }

  const renderItems = (items: readonly NavItem[]) =>
    items.map((item) => {
      if (item.underConstruction) {
        return (
          <button
            key={item.to}
            type="button"
            onClick={() => {
              setConstructionFeature(item.label);
            }}
            className="text-sidebar-foreground/80 hover:bg-sidebar-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:text-white"
          >
            <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge !== undefined && item.badge > 0 && (
              <span className="bg-destructive flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                {item.badge}
              </span>
            )}
          </button>
        );
      }

      return (
        <NavLink
          key={item.to}
          to={item.to}
          end
          onClick={closeMobile}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-sidebar-active text-white'
                : 'text-sidebar-foreground/80 hover:bg-sidebar-hover hover:text-white',
            )
          }
        >
          <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="flex-1">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className="bg-destructive flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
              {item.badge}
            </span>
          )}
        </NavLink>
      );
    });

  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={closeMobile}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            closeMobile();
          }
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close sidebar"
      />

      <aside className="bg-sidebar text-sidebar-foreground fixed inset-y-0 left-0 z-50 flex w-56 flex-col">
        <div className="flex items-center justify-between px-4 py-4">
          <img src={chartaiLogoLight} alt="ChartAI" className="h-7 w-auto" />
          <button
            type="button"
            onClick={closeMobile}
            className="text-sidebar-muted hover:bg-sidebar-hover flex items-center justify-center rounded-md p-1.5 transition-colors hover:text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <nav aria-label="Dashboard navigation" className="flex-1 overflow-y-auto px-2">
          {sections.map((section, sIdx) => (
            <div key={`section-${String(sIdx)}`}>
              {sIdx > 0 && <div className="border-sidebar-muted/20 mx-2 my-2 border-t" />}
              {renderItems(section.items)}
            </div>
          ))}
        </nav>

        {/* Messages */}
        <div className="border-sidebar-muted/20 border-t px-2 py-2">
          <button
            type="button"
            onClick={() => {
              setConstructionFeature('Messages');
            }}
            className="text-sidebar-foreground/80 hover:bg-sidebar-hover flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:text-white"
          >
            <MessageCircle className="h-5 w-5 shrink-0" />
            <span className="flex-1 text-left">Messages</span>
            <span className="bg-destructive flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
              3
            </span>
          </button>
        </div>

        {/* Profile at bottom */}
        <NavLink
          to="/dashboard/profiles"
          onClick={closeMobile}
          className="border-sidebar-muted/20 hover:bg-sidebar-hover border-t px-4 py-3 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-bold text-white">
              {getInitials(selectedProfile?.displayName ?? 'U')}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">
                {selectedProfile?.displayName ?? 'Select Profile'}
              </p>
              <p className="text-sidebar-muted truncate text-xs">Provider</p>
            </div>
          </div>
        </NavLink>
      </aside>

      <UnderConstructionDialog
        open={constructionFeature !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConstructionFeature(null);
          }
        }}
        featureName={constructionFeature ?? ''}
      />
    </div>
  );
}
